import { useState } from 'react';
import { Card, Typography, Button, Select, InputNumber, Space, theme, message } from '../../ui';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../../api/client';
import { themedCatAxis, themedValAxis, themedLegendText } from '../../lib/chartTheme';

const day10 = (v: any) => String(v ?? '').slice(0, 10); // normalise day/date keys to YYYY-MM-DD

export function ForecastsPage() {
  const { token } = theme.useToken();
  const [productId, setProductId] = useState<string | undefined>();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<any>(null);
  const [running, setRunning] = useState(false);

  const { data: products } = useQuery({
    // Active products only — the forecast target picker must not list archived/deactivated SKUs.
    queryKey: ['products-for-forecast', 'active'],
    queryFn: () => request<any>({ url: '/products', params: { pageSize: 200, active: 'true' } }),
  });

  const run = async () => {
    setRunning(true);
    try {
      const r = await request<any>({ url: '/ai/forecast/sales', method: 'POST', data: { productId, days } });
      setData(r);
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Forecast failed');
    } finally {
      setRunning(false);
    }
  };

  const option = data ? {
    grid: { left: 50, right: 20, top: 30, bottom: 40 },
    xAxis: { type: 'category', data: [...(data.history ?? []).map((h: any) => day10(h.day)), ...(data.forecast ?? []).map((f: any) => day10(f.date))], ...themedCatAxis(token) },
    yAxis: { type: 'value', ...themedValAxis(token) },
    tooltip: { trigger: 'axis' },
    legend: { data: ['History', 'Forecast', 'Upper', 'Lower'], top: 0, textStyle: themedLegendText(token) },
    series: [
      {
        name: 'History', type: 'line', smooth: true,
        data: [...(data.history ?? []).map((h: any) => Number(h.total)), ...(data.forecast ?? []).map(() => null)],
        itemStyle: { color: '#1F8A4C' },
      },
      {
        name: 'Forecast', type: 'line', smooth: true, lineStyle: { type: 'dashed' },
        data: [...(data.history ?? []).map(() => null), ...(data.forecast ?? []).map((f: any) => f.forecast)],
        itemStyle: { color: '#E7B22C' },
      },
      {
        name: 'Upper', type: 'line', smooth: true, lineStyle: { opacity: 0.3 },
        data: [...(data.history ?? []).map(() => null), ...(data.forecast ?? []).map((f: any) => f.upper)],
        itemStyle: { color: token.colorTextQuaternary },
      },
      {
        name: 'Lower', type: 'line', smooth: true, lineStyle: { opacity: 0.3 },
        data: [...(data.history ?? []).map(() => null), ...(data.forecast ?? []).map((f: any) => f.lower)],
        itemStyle: { color: token.colorTextQuaternary },
      },
    ],
  } : null;

  return (
    <div>
      <Typography.Title level={4}>Sales Forecast</Typography.Title>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Select
            allowClear placeholder="All products" style={{ width: 280 }}
            value={productId} onChange={setProductId} showSearch optionFilterProp="label"
            options={(products?.data ?? []).map((p: any) => ({ value: p.id, label: `${p.sku} · ${p.name}` }))}
          />
          <InputNumber min={7} max={90} value={days} onChange={(v) => setDays(Number(v ?? 30))} addonAfter="days" />
          <Button variant="primary" onClick={run} loading={running}>Forecast</Button>
        </Space>
        {data?.note && <Typography.Text type="warning">{data.note}</Typography.Text>}
        {option && <ReactECharts option={option} style={{ height: 180 }} />}
      </Card>
    </div>
  );
}
